import { OptionsFilter } from '@mantine/core';
import { FinchProviderDto, PayrollCycle, WaitingPeriod } from '@zorro/clients';
import { VALIDATION_MESSAGES, isDefined } from '@zorro/common-utils';
import { YesNo } from '@zorro/types';
import * as yup from 'yup';

export const HRIS_PROVIDER_NOT_LISTED = 'NOT_LISTED';

export const getCompanySetupSchemaObject = (isFinalizationMode: boolean) => {
  return yup.object({
    subEntities: yup.array().of(yup.string().required()).required(),
    subEntities2: yup.array().of(yup.string().required()).required(),
    hrisProvider: yup.string().nullable(),
    hrisProviderNotListedText: yup
      .string()
      .nullable()
      .when('hrisProvider', {
        is: HRIS_PROVIDER_NOT_LISTED,
        then: (schema) =>
          schema
            .trim()
            .required(VALIDATION_MESSAGES.hrisProviderNotListedTextRequired),
      }),
    ...(isFinalizationMode
      ? {
          payrollCycle: yup
            .mixed<PayrollCycle>()
            .oneOf(
              Object.values(PayrollCycle),
              VALIDATION_MESSAGES.payrollCycleRequired
            )
            .typeError(VALIDATION_MESSAGES.payrollCycleRequired)
            .required(VALIDATION_MESSAGES.payrollCycleRequired),
          waitingPeriod: yup
            .mixed<WaitingPeriod>()
            .oneOf(
              Object.values(WaitingPeriod),
              VALIDATION_MESSAGES.waitingPeriodRequired
            )
            .typeError(VALIDATION_MESSAGES.waitingPeriodRequired)
            .required(VALIDATION_MESSAGES.waitingPeriodRequired),
          isApplicableLargeEmployer: yup
            .mixed<YesNo>()
            .oneOf(Object.values(YesNo), VALIDATION_MESSAGES.aleRequired)
            .typeError(VALIDATION_MESSAGES.aleRequired)
            .required(VALIDATION_MESSAGES.aleRequired),
        }
      : {
          payrollCycle: yup
            .mixed<PayrollCycle>()
            .oneOf(
              Object.values(PayrollCycle),
              VALIDATION_MESSAGES.payrollCycleRequired
            )
            .typeError(VALIDATION_MESSAGES.payrollCycleRequired)
            .nullable(),
          waitingPeriod: yup
            .mixed<WaitingPeriod>()
            .oneOf(
              Object.values(WaitingPeriod),
              VALIDATION_MESSAGES.waitingPeriodRequired
            )
            .typeError(VALIDATION_MESSAGES.waitingPeriodRequired)
            .nullable(),
          isApplicableLargeEmployer: yup
            .mixed<YesNo>()
            .oneOf(Object.values(YesNo), VALIDATION_MESSAGES.aleRequired)
            .typeError(VALIDATION_MESSAGES.aleRequired)
            .nullable(),
        }),
  });
};

export type CompanySetupFormFields = yup.InferType<
  ReturnType<typeof getCompanySetupSchemaObject>
>;

export const getHrisProviderOptions = (
  providers: FinchProviderDto[],
  selectedHrisProvider?: string | null
) => {
  const isCustomSelected =
    isDefined(selectedHrisProvider) &&
    selectedHrisProvider !== HRIS_PROVIDER_NOT_LISTED &&
    providers.every((provider) => provider.id !== selectedHrisProvider);

  const customOption =
    isCustomSelected && selectedHrisProvider
      ? [{ value: selectedHrisProvider, label: selectedHrisProvider }]
      : [];

  const sortedProviderOptions = providers
    .map(({ id, displayName }) => ({ value: id, label: displayName }))
    .sort((left, right) => left.label.localeCompare(right.label));

  return [
    ...customOption,
    ...sortedProviderOptions,
    { value: HRIS_PROVIDER_NOT_LISTED, label: 'Other' },
  ];
};

export const filterHrisProviderOptions: OptionsFilter = ({
  options,
  search,
}) => {
  const query = search.trim().toLowerCase();

  return options.filter((option) => {
    if ('value' in option) {
      const isOtherOption = option.value === HRIS_PROVIDER_NOT_LISTED;
      const matchesQuery = option.label.toLowerCase().includes(query);

      return isOtherOption || matchesQuery;
    }

    return true;
  });
};

export const getHrisProviderForUpdate = (
  hrisProvider: string | null | undefined,
  hrisProviderNotListedText: string | null | undefined
) => {
  if (hrisProvider === HRIS_PROVIDER_NOT_LISTED) {
    return hrisProviderNotListedText?.trim() || null;
  }

  return hrisProvider || null;
};
